import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollText, Grid, Settings, Trophy, CalendarDays, Mail, History } from 'lucide-react';
import { getLevelForPoints, ladderPointsFor } from '../../../config/levelsConfig';
import LevelAnimalIcon from './LevelAnimalIcon';
import { getStudentDisplayName } from '../../../config/studentNameMapping';
import { getClassLeaderboard } from '../api/students';
import './MobileStudentNav.css';

/**
 * Animated counter that ticks up/down to target value
 * Reports animation state via onAnimatingChange callback
 */
function AnimatedCounter({ value, duration = 500, onAnimatingChange }) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    const diff = value - prevValue;

    if (diff === 0) return;

    setIsAnimating(true);
    onAnimatingChange?.(true, diff > 0); // Report animation start and direction

    const steps = Math.abs(diff);
    const stepDuration = Math.min(duration / steps, 150); // Cap at 150ms per step for slower counting
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const newValue = prevValue + (diff > 0 ? currentStep : -currentStep);
      setDisplayValue(newValue);

      if (currentStep >= steps) {
        clearInterval(timer);
        prevValueRef.current = value;
        setTimeout(() => {
          setIsAnimating(false);
          onAnimatingChange?.(false, false);
        }, 300);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [value, duration, onAnimatingChange]);

  // Key changes on each tick to retrigger animation
  return (
    <span key={displayValue} className={isAnimating ? 'counting' : ''}>
      {displayValue}
    </span>
  );
}

/**
 * Trophy icon for leaderboard access
 */
function LevelBadgeCompact() {
  return (
    <div className="level-badge-compact">
      <Trophy size={22} className="trophy-icon" />
    </div>
  );
}

/**
 * Subtle top bar for less-used actions (History, Performances, Cartoons, Settings)
 * Non-sticky, scrolls with content, very minimal
 */
/**
 * Leaderboard bottom sheet — single scrollable view
 * Each class card shows rank, name, score, then a 2-column grid of students
 */
function LeaderboardSheet({ studentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getClassLeaderboard(studentId)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData({ myClassIds: [], classes: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId]);

  const myClassIds = data?.myClassIds || [];
  // Filter: minimum 2 students and 10 weekly points to appear on leaderboard
  const eligibleClasses = (data?.classes || []).filter(
    cls => (cls.students?.length || 0) >= 2 && cls.weeklyScore >= 10
  );

  return (
    <div className="level-sheet-overlay" onClick={onClose} data-testid="leaderboard-sheet-overlay">
      <div className="leaderboard-sheet level-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="level-sheet-handle" />
        <CoinInfoBox />

        <div className="leaderboard-header">
          <span className="leaderboard-title">本周班级排行</span>
        </div>

        <div className="leaderboard-list">
          {loading ? (
            <div className="leaderboard-loading">加载中...</div>
          ) : eligibleClasses.length === 0 ? (
            <div className="leaderboard-empty">暂无数据</div>
          ) : (
            eligibleClasses.map((cls, i) => {
              const rank = i + 1;
              const isMyClass = myClassIds.includes(cls.classId);
              return (
                <div
                  key={cls.classId}
                  className={`leaderboard-class-card${isMyClass ? ' my-class' : ''}`}
                  data-testid={`class-row-${cls.classId}`}
                >
                  <div className="leaderboard-class-header">
                    <span className={`leaderboard-rank${rank <= 3 ? ' gold' : ''}`}>
                      {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                    </span>
                    <span className="leaderboard-name">{cls.className}</span>
                    <span className="leaderboard-score">{cls.weeklyScore}</span>
                  </div>
                  {cls.students?.length > 0 && (
                    <div className="leaderboard-students-grid">
                      {cls.students.map(s => {
                        const level = getLevelForPoints(s.totalScore);
                        const isCurrent = s.studentId === studentId;
                        return (
                          <div
                            key={s.studentId}
                            className={`leaderboard-student-cell${isCurrent ? ' current' : ''}`}
                          >
                            <span className="leaderboard-student-icon">{level.icon}</span>
                            <span className="leaderboard-student-name">
                              {getStudentDisplayName(s.studentId, s.studentName)}
                            </span>
                            {/* <span className="leaderboard-student-score">{s.weeklyScore}</span> */}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// beiski's top-of-the-ladder preview lives in levelsConfig, shared with kids songs (Rob, 2026-10-16)
export { ladderPointsFor };

export const MobileStudentTopBar = forwardRef(function MobileStudentTopBar(
  { studentId, displayName, totalCoins, coinRef, isTeacherView = false, onSettingsClick, onCalendarClick, onNotificationClick, onHistoryClick, unseenCount = 0 },
  ref
) {
  const navigate = useNavigate();
  const [isReceivingCoins, setIsReceivingCoins] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const handleLevelClick = () => {
    if (!isTeacherView) {
      setShowLeaderboard((prev) => !prev);
    }
  };

  // The avatar opens the child's homepage (Rob, 2026-10-30: a page, not the level sheet).
  // Name and 积分 go along so its top paints before the profile request returns.
  const handleAvatarClick = () => {
    if (!isTeacherView) {
      navigate(`/profile/${studentId}`, { state: { name: displayName, points: ladderPoints } });
    }
  };

  const handleXpAnimatingChange = (isAnimating, isIncreasing) => {
    // Only show receiving animation when XP is increasing
    setIsReceivingCoins(isAnimating && isIncreasing);
  };

  const ladderPoints = ladderPointsFor(studentId, totalCoins);
  const level = getLevelForPoints(ladderPoints);

  return (
    <div className={`mobile-student-top-bar${isTeacherView ? ' teacher-view' : ''}`}>
      {/* Avatar with name - opens the child's homepage */}
      <div
        className={`top-bar-avatar${isTeacherView ? '' : ' clickable'}`}
        onClick={handleAvatarClick}
        role={isTeacherView ? undefined : 'button'}
      >
        <div
          className={`avatar-circle${level.variant ? ` ${level.variant}` : ''}`}
          style={!level.variant ? {
            background: `linear-gradient(145deg, ${level.colorLight} 0%, ${level.color} 100%)`,
            boxShadow: `0 2px 0 ${level.colorDark}, 0 3px 6px ${level.color}40`,
          } : undefined}
        >
          {/* the real pixel animal (its emoji until the sprite arrives) — Rob, 2026-10-16 */}
          <LevelAnimalIcon level={level.level} icon={level.icon} />
        </div>
        <span className="avatar-name">{displayName || '同学'}</span>
      </div>

      {/* Trophy + Leaderboard hidden for now — ship later */}
      {false && totalCoins !== undefined && (
        <div
          ref={coinRef}
          className={`top-bar-level${isTeacherView ? ' readonly' : ''}${isReceivingCoins ? ' receiving' : ''}`}
          onClick={handleLevelClick}
          role={isTeacherView ? undefined : 'button'}
        >
          <LevelBadgeCompact
            points={totalCoins}
            isReceiving={isReceivingCoins}
            onAnimatingChange={handleXpAnimatingChange}
            AnimatedCounter={AnimatedCounter}
          />
        </div>
      )}

      {/* Leaderboard bottom sheet */}
      {false && showLeaderboard && createPortal(
        <LeaderboardSheet
          studentId={studentId}
          onClose={() => setShowLeaderboard(false)}
        />,
        document.body
      )}

      {/* Right-side action buttons */}
      <div className="top-bar-actions">
        <button className="top-bar-btn" onClick={onNotificationClick} aria-label="批改反馈" type="button">
          <Mail size={22} />
          {unseenCount > 0 && <span className="top-bar-badge">{unseenCount}</span>}
        </button>
        <button className="top-bar-btn" onClick={onHistoryClick} aria-label="历史" type="button">
          <History size={22} />
        </button>
        <button className="top-bar-btn" onClick={onCalendarClick} aria-label="日历" type="button">
          <CalendarDays size={22} />
        </button>
        <button className="top-bar-btn" onClick={onSettingsClick} aria-label="设置" type="button">
          <Settings size={22} />
        </button>
      </div>
    </div>
  );
});

/**
 * Mobile bottom navigation for student dashboard
 * Shows nav buttons for Home, Map, History
 * (Performances, Cartoons, Settings are in the top bar)
 */
export const MobileStudentNav = forwardRef(function MobileStudentNav(
  { studentId, mapRef, dailyCount = 0, isTeacherView = false, activeTab: propActiveTab, onTabChange },
  ref
) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL path segment after studentId (for student view)
  // or use prop (for teacher view)
  const pathAfterStudent = location.pathname.split('/').pop();
  const activeTab = propActiveTab || (
    ['map', 'settings', 'performances', 'cartoons', 'levels'].includes(pathAfterStudent)
      ? pathAfterStudent
      : 'daily'
  );

  const handleNavClick = (tab) => {
    if (isTeacherView && onTabChange) {
      // Teacher view: use local state
      onTabChange(tab);
    } else {
      // Student view: navigate via URL
      const path = tab === 'daily' ? `/dashboard/${studentId}` : `/dashboard/${studentId}/${tab}`;
      navigate(path);
    }
  };

  return (
    <div className={`mobile-student-nav-container${isTeacherView ? ' teacher-view' : ''}`}>
      <nav className="mobile-student-nav">
        <button
          className={`nav-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => handleNavClick('daily')}
          aria-label="作业"
        >
          <div className="nav-icon-wrapper">
            <ScrollText size={22} />
            {dailyCount > 0 && <span className="nav-badge">{dailyCount}</span>}
          </div>
          <span>作业</span>
        </button>

        {/* Hidden — vocab tab not ready
        <button
          ref={mapRef}
          className={`nav-btn ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => handleNavClick('map')}
          aria-label="词汇"
        >
          <Grid size={22} />
          <span>词汇</span>
        </button>
        */}

        <button
          className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => handleNavClick('settings')}
          aria-label="设置"
        >
          <Settings size={22} />
          <span>设置</span>
        </button>

      </nav>
    </div>
  );
});

export default MobileStudentNav;
